// Conteo físico — session list (index route: /inventory/conteo).
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Badge, Button, Card, Select, SelectItem,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import AccessDenied from '../components/AccessDenied';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { deleteSession, listSessions } from '../api/inventoryCount';
import { getStores } from '../api/priceUpload';
import { countStatusColor, countStatusLabel, formatCountScope } from '../lib/format';
import { canUseCount } from '../lib/roles';
import { t } from '../lib/i18n';
import type { CountSession, Store, User } from '../api/types';

type StatusKey = keyof typeof t.conteo.status;
const STATUSES = Object.keys(t.conteo.status) as StatusKey[];

export default function InventoryCount() {
  const user = useOutletContext<User>();
  if (!canUseCount(user)) return <AccessDenied user={user} />;
  return <InventoryCountInner />;
}

function InventoryCountInner() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<CountSession[] | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | StatusKey>('all');
  const [storeFilter, setStoreFilter] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CountSession | null>(null);

  const load = () => {
    setErr(null);
    setSessions(null);
    listSessions()
      .then(setSessions)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    getStores().then(setStores).catch(() => { /* store names are a nicety — sid fallback below */ });
  }, []);

  const storeName = (sid: number) => stores.find((s) => s.sid === sid)?.name ?? `#${sid}`;

  const storeSids = useMemo(
    () => Array.from(new Set((sessions ?? []).map((s) => s.sid))).sort((a, b) => a - b),
    [sessions],
  );

  const rows = useMemo(() => {
    let pool = sessions ?? [];
    if (statusFilter !== 'all') pool = pool.filter((s) => s.status === statusFilter);
    if (storeFilter !== null) pool = pool.filter((s) => s.sid === storeFilter);
    return [...pool].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  }, [sessions, statusFilter, storeFilter]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await deleteSession(target.id);
      load();
    } catch (e) {
      setErr((e as Error).message ?? t.conteo.errors.generic);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Title>{t.conteo.title}</Title>
          <Text className="text-tremor-content text-sm">{t.conteo.subtitle}</Text>
        </div>
        <Link to="/inventory/conteo/nuevo">
          <Button>+ {t.conteo.newSession}</Button>
        </Link>
      </div>

      {sessions !== null && sessions.length > 0 && (
        <Card className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.conteo.filters.status}</div>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as 'all' | StatusKey)}
              enableClear={false}
              className="w-44"
            >
              <SelectItem value="all">{t.conteo.filters.statusAll}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{t.conteo.status[s]}</SelectItem>
              ))}
            </Select>
          </label>
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.conteo.filters.store}</div>
            <Select
              value={storeFilter === null ? 'all' : String(storeFilter)}
              onValueChange={(v) => setStoreFilter(v === 'all' ? null : Number(v))}
              enableClear={false}
              className="w-52"
            >
              <SelectItem value="all">{t.conteo.filters.storeAll}</SelectItem>
              {storeSids.map((sid) => (
                <SelectItem key={sid} value={String(sid)}>{storeName(sid)}</SelectItem>
              ))}
            </Select>
          </label>
        </Card>
      )}

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && sessions === null && <TableSkeleton rows={6} cols={7} />}
      {!err && sessions !== null && rows.length === 0 && <EmptyState message={t.conteo.empty} />}
      {!err && rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.conteo.cols.name}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.cols.store}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.cols.scope}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.cols.status}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.cols.createdBy}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.cols.createdAt}</TableHeaderCell>
                  <TableHeaderCell>{t.conteo.cols.actions}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((s) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer hover:bg-tremor-background-muted"
                    onClick={() => navigate(`/inventory/conteo/${s.id}`)}
                  >
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>{storeName(s.sid)}</TableCell>
                    <TableCell>{formatCountScope(s)}</TableCell>
                    <TableCell>
                      <Badge color={countStatusColor(s.status)}>{countStatusLabel(s.status)}</Badge>
                    </TableCell>
                    <TableCell>{s.created_by || '—'}</TableCell>
                    <TableCell className="text-sm">
                      {s.created_at ? new Date(s.created_at).toLocaleString('es-MX') : '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="xs"
                        variant="secondary"
                        color="red"
                        onClick={(e) => { e.stopPropagation(); setPendingDelete(s); }}
                      >
                        {t.conteo.deleteConfirm.confirm}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t.conteo.deleteConfirm.title}
          message={t.conteo.deleteConfirm.message.replace('{name}', pendingDelete.name)}
          confirmLabel={t.conteo.deleteConfirm.confirm}
          cancelLabel={t.conteo.deleteConfirm.cancel}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
